import { useEffect, useCallback } from 'react';
import {
  MousePointer2,
  Circle,
  Square,
  Minus,
  Pentagon,
  MapPin,
  Undo2,
  Redo2,
  Trash2,
  Search,
  Crosshair,
} from 'lucide-react';
import { useMapStore, type DrawingTool } from '@/stores/mapStore';
import { useCollaborationStore } from '@/stores/collaborationStore';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const tools: { id: DrawingTool; icon: React.ReactNode; label: string; shortcut?: string }[] = [
  { id: 'select', icon: <MousePointer2 size={16} />, label: 'Select', shortcut: '1' },
  { id: 'point', icon: <MapPin size={16} />, label: 'Point', shortcut: '2' },
  { id: 'line', icon: <Minus size={16} />, label: 'Line', shortcut: '3' },
  { id: 'polygon', icon: <Pentagon size={16} />, label: 'Polygon', shortcut: '4' },
  { id: 'rectangle', icon: <Square size={16} />, label: 'Rectangle', shortcut: '5' },
  { id: 'circle', icon: <Circle size={16} />, label: 'Circle', shortcut: '6' },
  { id: 'ballistic', icon: <Crosshair size={16} />, label: 'Ballistic', shortcut: '7' },
];

export function Toolbar() {
  const { activeTool, setActiveTool, selection, clearSelection } = useMapStore();
  const {
    setActiveTool: setAwarenessTool,
    ydoc,
    getAnnotations,
    undo,
    redo,
    canUndo,
    canRedo,
    roomName,
  } = useCollaborationStore();
  const localUser = useCollaborationStore((s) => s.localUser);

  const handleToolSelect = (tool: DrawingTool) => {
    setActiveTool(tool);
    setAwarenessTool(tool === 'select' ? null : tool);
  };

  const handleUndo = useCallback(() => { undo(); }, [undo]);
  const handleRedo = useCallback(() => { redo(); }, [redo]);

  const handleDelete = useCallback(() => {
    if (selection.featureIds.length === 0 || !ydoc) return;

    const annotations = getAnnotations();
    if (!annotations) return;

    ydoc.transact(() => {
      const arr = annotations.toArray();
      const indicesToDelete: number[] = [];

      for (let i = 0; i < arr.length; i++) {
        if (selection.featureIds.includes(arr[i].properties?.id || '')) {
          indicesToDelete.push(i);
        }
      }

      indicesToDelete.sort((a, b) => b - a);
      for (const index of indicesToDelete) {
        annotations.delete(index, 1);
      }
    });

    clearSelection();
  }, [selection.featureIds, ydoc, getAnnotations, clearSelection]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection.featureIds.length > 0) {
        e.preventDefault();
        handleDelete();
        return;
      }

      // Tool shortcuts
      const key = e.key.toLowerCase();
      for (const tool of tools) {
        if (tool.shortcut && tool.shortcut.toLowerCase() === key) {
          e.preventDefault();
          handleToolSelect(tool.id);
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selection.featureIds, handleDelete, handleUndo, handleRedo]);

  return (
    <header className="h-11 flex items-center px-3 gap-1.5 bg-card border-b border-border">
      {/* Logo */}
      <div className="flex items-center gap-2 pr-3 mr-1">
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
          <div className="w-2 h-2 rounded-full border-[1.5px] border-white" />
        </div>
        <span className="text-sm font-semibold text-foreground tracking-tight">
          Overwatch
        </span>
      </div>

      <Separator orientation="vertical" className="h-5" />

      {/* Drawing tools */}
      <div className="flex items-center gap-0.5 px-1">
        {tools.map((tool) => (
          <Tooltip key={tool.id}>
            <TooltipTrigger
              className={cn('tool-btn', activeTool === tool.id && 'active')}
              onClick={() => handleToolSelect(tool.id)}
            >
              {tool.icon}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {tool.label}{tool.shortcut && <span className="ml-1.5 opacity-60">{tool.shortcut}</span>}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>

      <Separator orientation="vertical" className="h-5" />

      {/* Edit actions */}
      <div className="flex items-center gap-0.5 px-1">
        <Tooltip>
          <TooltipTrigger
            className={cn('tool-btn', !canUndo && 'opacity-30 cursor-not-allowed')}
            onClick={handleUndo}
            disabled={!canUndo}
          >
            <Undo2 size={16} />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Undo <span className="opacity-60">Ctrl+Z</span></TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            className={cn('tool-btn', !canRedo && 'opacity-30 cursor-not-allowed')}
            onClick={handleRedo}
            disabled={!canRedo}
          >
            <Redo2 size={16} />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Redo <span className="opacity-60">Ctrl+Shift+Z</span></TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            className={cn('tool-btn', selection.featureIds.length === 0 && 'opacity-30 cursor-not-allowed')}
            onClick={handleDelete}
            disabled={selection.featureIds.length === 0}
          >
            <Trash2 size={16} />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Delete <span className="opacity-60">Del</span></TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1" />

      {/* Command palette trigger */}
      <button
        onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
        className="flex items-center gap-2 px-2.5 py-1 mr-2 rounded-md border border-border bg-muted/50 hover:bg-accent/50 transition-colors text-xs text-muted-foreground"
      >
        <Search size={12} />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden sm:inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px]">⌘K</kbd>
      </button>

      {/* Room */}
      <span className="text-xs text-muted-foreground mr-2">
        {roomName || 'default-operation'}
      </span>

      <Separator orientation="vertical" className="h-5" />

      {/* User */}
      {localUser && (
        <div className="flex items-center gap-2 pl-2">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white"
            style={{ backgroundColor: localUser.color }}
          >
            {localUser.name.charAt(0).toUpperCase()}
          </div>
          <span className="text-xs text-foreground">{localUser.name}</span>
        </div>
      )}
    </header>
  );
}
